import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/src/lib/database.types';

export const PAYMENT_QR_BUCKET = 'payment-qr-codes';

// Allowed MIME types for QR code images
const ALLOWED_QR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_QR_SIZE = 2 * 1024 * 1024; // 2MB

/**
 * Validate QR code file
 *
 * @param file File to validate
 * @throws Error if file is invalid
 */
export function isValidQRFile(file: File): void {
  // Check file type
  if (!ALLOWED_QR_TYPES.includes(file.type)) {
    throw new Error(
      `Invalid file type. Allowed types: ${ALLOWED_QR_TYPES.join(', ')}`
    );
  }

  // Check file size
  if (file.size > MAX_QR_SIZE) {
    throw new Error(`File size exceeds 2MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
  }
}

/**
 * Build storage path for payment QR code
 * Format: user_id/payment-qr.ext
 * Single QR per user (upsert replaces existing)
 *
 * @param userId User ID
 * @param extension File extension (e.g., 'jpg', 'png')
 * @returns Storage path
 */
export function buildPaymentQRPath(userId: string, extension: string): string {
  const ext = extension.toLowerCase().replace(/^\./, '');
  return `${userId}/payment-qr.${ext}`;
}

/**
 * Upload payment QR code to Supabase Storage
 * Replaces existing QR code if present (upsert: true)
 *
 * @param supabase Supabase client
 * @param userId User ID
 * @param file File to upload
 * @returns Storage path
 */
export async function uploadPaymentQR(
  supabase: SupabaseClient<Database>,
  userId: string,
  file: File,
): Promise<string> {
  // Validate file
  isValidQRFile(file);

  // Get file extension
  const extension = file.name.split('.').pop() || 'jpg';
  const storagePath = buildPaymentQRPath(userId, extension);

  // Convert file to buffer
  const buffer = Buffer.from(await file.arrayBuffer());
  const contentType = file.type || 'application/octet-stream';

  // Upload with upsert to replace existing
  const { error } = await supabase.storage
    .from(PAYMENT_QR_BUCKET)
    .upload(storagePath, buffer, {
      contentType,
      upsert: true, // Replace existing QR code
    });

  if (error) {
    throw error;
  }

  return storagePath;
}

/**
 * Remove payment QR code from Supabase Storage
 *
 * @param supabase Supabase client
 * @param storagePath Storage path to remove
 */
export async function removePaymentQR(
  supabase: SupabaseClient<Database>,
  storagePath?: string | null,
): Promise<void> {
  if (!storagePath) return;

  const { error } = await supabase.storage
    .from(PAYMENT_QR_BUCKET)
    .remove([storagePath]);

  if (error) {
    console.error('Failed to remove payment QR code:', error);
    throw error;
  }
}

/**
 * Create signed URL for payment QR code
 *
 * @param supabase Supabase client
 * @param storagePath Path in payment-qr-codes bucket (e.g., "user_id/payment-qr.jpg")
 * @param expiresInSeconds URL expiration time in seconds (default: 1 hour)
 * @returns Signed URL or null if failed
 */
export async function createSignedQRUrl(
  supabase: SupabaseClient<Database>,
  storagePath?: string | null,
  expiresInSeconds = 3600, // 1 hour
): Promise<string | null> {
  if (!storagePath) return null;

  try {
    const { data, error } = await supabase.storage
      .from(PAYMENT_QR_BUCKET)
      .createSignedUrl(storagePath, expiresInSeconds);

    if (error) {
      console.error('Error creating signed URL for payment QR:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Exception creating signed URL for payment QR:', error);
    return null;
  }
}
